import { useCallback, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { NV_BRAND_UNDERLINE_CLASS, NV_PATHS, NvAlertIcon, NvPwcLogoMark } from "@/components/navigator";
import { SpinnerOverlay } from "@/components/ui/spinner-overlay";
import { useAuth } from "@/contexts/auth-context";
import { useSsoLogin, type SsoLoginType } from "@/hooks/use-sso-login";
import { getApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";

// F06 로그인 (Figma ver.7 847:45208) + 로그인 불가 안내 영역 (847:45244, 설계서 Front v0.5 slide 87: 미가입 · 승인 대기 · 반려 · 비활성 · 탈퇴 5종).
// 서버가 로그인을 거절하면(404/403 + 코드) 별도 팝업 없이 이 화면의 안내 영역에 사유를 보여 줘요.
// ver.7: 버튼마다 대상 안내(고객사 · 공급사 / PwC 임직원), [PwC SSO 로그인]에 PwC 로고, "로그인이 안 되나요? 로그인 불가 안내 ›" 링크.
// 이 링크가 어디로 이어지는지는 Figma에 정의가 없어 5종 안내 문구를 이 화면 안에서 펼쳐 보여 줘요(대상 확정 전 임시).
interface Guidance {
  title: string;
  description: string;
  action?: "signup" | "contact";
}

const GUIDANCE: Record<string, Guidance> = {
  NOT_REGISTERED: {
    title: "가입되지 않은 계정입니다.",
    description: "회원가입 후 관리자 승인이 완료되면 로그인할 수 있습니다.",
    action: "signup",
  },
  ACCOUNT_PENDING: { title: "가입 검토가 진행 중입니다.", description: "승인 결과는 가입하신 메일로 안내드립니다." },
  ACCOUNT_REJECTED: { title: "가입이 반려되었습니다.", description: "사유는 메일을 확인해 주세요." },
  ACCOUNT_INACTIVE: {
    title: "비활성화된 계정입니다.",
    description: "관리자에게 문의해 주세요.",
    action: "contact",
  },
  ACCOUNT_WITHDRAWN: {
    title: "탈퇴한 계정입니다.",
    description: "같은 이메일로 다시 가입할 수 있습니다.",
    action: "signup",
  },
  // 로그인 버튼과 계정 유형이 다를 때: PwC 임직원은 PwC SSO, 수요자 · 공급자는 Outlook
  USE_PWC_SSO: {
    title: "PwC 임직원 계정입니다.",
    description: "[PwC SSO 로그인] 버튼으로 로그인해 주세요.",
  },
  USE_OUTLOOK_LOGIN: {
    title: "PwC SSO 로그인은 PwC 임직원 전용입니다.",
    description: "[Outlook 계정으로 로그인] 버튼으로 로그인해 주세요.",
  },
};

function guidanceOf(error: unknown): Guidance {
  const apiError = getApiError(error);
  if (apiError && GUIDANCE[apiError.code]) return GUIDANCE[apiError.code];
  const message = apiError?.message ?? (error instanceof Error ? error.message : null);
  return { title: "로그인하지 못했습니다.", description: message || "잠시 후 다시 시도해 주세요." };
}

// 로그인 불가 안내 링크를 누르면 펼쳐 보이는 상태별 안내 (Figma 847:45244 "상태별 안내 문구" 5종 그대로)
const GUIDANCE_LIST: { label: string; code: string }[] = [
  { label: "미가입", code: "NOT_REGISTERED" },
  { label: "승인 대기", code: "ACCOUNT_PENDING" },
  { label: "반려", code: "ACCOUNT_REJECTED" },
  { label: "비활성", code: "ACCOUNT_INACTIVE" },
  { label: "탈퇴", code: "ACCOUNT_WITHDRAWN" },
];

function MicrosoftMark() {
  return (
    <span aria-hidden className="grid h-[18px] w-[18px] grid-cols-2 gap-[2px]">
      <span className="bg-[#F25022]" />
      <span className="bg-[#7FBA00]" />
      <span className="bg-[#00A4EF]" />
      <span className="bg-[#FFB900]" />
    </span>
  );
}

export function NavigatorLoginPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [guidance, setGuidance] = useState<Guidance | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const redirect = searchParams.get("redirect") || NV_PATHS.home;

  const onSuccess = useCallback((returnTo: string) => navigate(returnTo, { replace: true }), [navigate]);
  const onError = useCallback(
    (error: unknown) => {
      setGuidance(guidanceOf(error));
      navigate("/login", { replace: true });
    },
    [navigate],
  );
  const { isCallback, isRedirectFlow, processing, startLogin } = useSsoLogin({ onSuccess, onError });

  if (isCallback) return isRedirectFlow || processing ? <SpinnerOverlay /> : null;
  if (!isLoading && isAuthenticated && !processing) return <Navigate to={redirect} replace />;

  const login = (type: SsoLoginType) => {
    setGuidance(null);
    startLogin(type, redirect);
  };

  return (
    <div className="flex flex-1 justify-center bg-nv-bg-page px-4 pb-24 pt-[72px]">
      {processing && <SpinnerOverlay />}
      <section className="flex h-fit w-full max-w-[480px] flex-col gap-4 rounded-nv-card border border-nv-border bg-nv-surface p-10">
        <h1 className="text-[26px] font-normal leading-[31px] text-nv-text-primary">로그인</h1>
        <p className="text-[14px] leading-[17px] text-nv-text-secondary">계정 유형에 맞는 로그인 방식을 선택하세요</p>

        <div className="flex flex-col items-center gap-4 pt-2">
          <div className="flex flex-col items-center gap-[6px]">
            <button
              type="button"
              onClick={() => login("outlook")}
              className="inline-flex h-12 w-[280px] items-center justify-center gap-[10px] rounded-nv-control bg-nv-text-primary px-6 text-[15px] font-bold leading-[18px] text-nv-text-inverse hover:bg-black"
            >
              <MicrosoftMark />
              Outlook 계정으로 로그인
            </button>
            <p className="text-[12px] leading-[14px] text-[#6B7078]">고객사 · 공급사 회원 (가입 시 인증한 회사 이메일)</p>
          </div>
          <div className="flex flex-col items-center gap-[6px]">
            <button
              type="button"
              onClick={() => login("pwc_sso")}
              className="inline-flex h-12 w-[280px] items-center justify-center gap-[10px] rounded-nv-control border border-nv-border-strong bg-nv-surface px-6 text-[15px] font-medium leading-[18px] text-nv-text-primary hover:bg-nv-bg-subtle"
            >
              <NvPwcLogoMark />
              PwC SSO 로그인
            </button>
            <p className="text-[12px] leading-[14px] text-[#6B7078]">PwC 임직원 (사내 계정)</p>
          </div>
        </div>

        <hr className="border-nv-divider" />
        <p className="flex items-center justify-center gap-[6px] text-[13px] leading-4">
          <span className="text-nv-text-secondary">아직 회원이 아니신가요?</span>
          <Link to={NV_PATHS.signup} className={cn("font-bold", NV_BRAND_UNDERLINE_CLASS, guidance?.action === "signup" ? "text-nv-brand" : "text-nv-text-primary")}>
            회원가입 ›
          </Link>
        </p>
        <p className="flex items-center justify-center gap-[6px] text-[13px] leading-4">
          <span className="text-[#6B7078]">로그인이 안 되나요?</span>
          <button type="button" aria-expanded={guideOpen} onClick={() => setGuideOpen((open) => !open)} className={cn("font-bold text-nv-text-primary", NV_BRAND_UNDERLINE_CLASS)}>
            로그인 불가 안내 ›
          </button>
        </p>
        {guideOpen && (
          <ul className="flex flex-col gap-2 rounded-nv-card bg-nv-bg-page px-[14px] py-3">
            {GUIDANCE_LIST.map(({ label, code }) => (
              <li key={code} className="text-[12px] leading-[17px] text-nv-text-secondary">
                <span className="font-bold text-nv-text-primary">{label}</span>
                {" · "}
                {GUIDANCE[code].title} {GUIDANCE[code].description}
              </li>
            ))}
          </ul>
        )}

        {guidance && (
          <div role="alert" className="flex flex-col gap-1 rounded-nv-card border border-[#FFCDA8] bg-[#F3F4F6] px-[14px] py-3">
            <p className="inline-flex items-center gap-[6px] text-[13px] font-bold leading-4 text-nv-danger">
              <NvAlertIcon size={14} className="shrink-0" />
              {guidance.title}
            </p>
            <p className="text-[12px] leading-[14px] text-nv-danger">{guidance.description}</p>
            {guidance.action === "contact" && (
              <Link to={NV_PATHS.contact} className="text-[12px] font-bold leading-[14px] text-nv-danger underline">
                문의하기 (비회원) ›
              </Link>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
